import { useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStoreActions } from '~/contexts';
import { ErrorResponse } from '~/infra/ErrorResponse';
import { isObject } from '~/infra/isObject';
import { useDraftsAiSearchStore } from '../../hooks/useDraftsAiSearchStore';
const ERROR_CODES = new Set([
    'feature_disabled',
    'llm_timeout',
    'llm_error',
    'llm_invalid_response',
    'llm_schema_validation_failed'
]);
const isErrorCode = (value)=>typeof value === 'string' && ERROR_CODES.has(value);
const errorCodeFromResponseBody = (body)=>{
    if (isObject(body) && isErrorCode(body.error)) {
        return body.error;
    }
    return 'unknown';
};
const DRAFTS_AI_SEARCH_TIMEOUT_MS = 10000;
const ABORT_ERROR_NAMES = new Set([
    'CanceledError',
    'AbortError'
]);
const ABORT_ERROR_CODES = new Set([
    'ERR_CANCELED'
]);
const TIMEOUT_ERROR_CODES = new Set([
    'ECONNABORTED',
    'ETIMEDOUT'
]);
const isAbortError = (err)=>{
    if (!isObject(err)) return false;
    return typeof err.name === 'string' && ABORT_ERROR_NAMES.has(err.name) || typeof err.code === 'string' && ABORT_ERROR_CODES.has(err.code);
};
const isTimeoutError = (err)=>isObject(err) && typeof err.code === 'string' && TIMEOUT_ERROR_CODES.has(err.code);
export const useDraftsAiSearchSubmit = ()=>{
    const { api } = useStoreActions();
    // WHY: a render-stable holder for the in-flight AbortController (replaces useRef, which the
    // zustand convention bans for mirroring mutable values); the lazy initializer runs once.
    const [inFlight] = useState(()=>({
            controller: null
        }));
    const { setSubmitting, setFilter, setError, setAbortInFlight } = useDraftsAiSearchStore(useShallow((s)=>({
            setSubmitting: s.setSubmitting,
            setFilter: s.setFilter,
            setError: s.setError,
            setAbortInFlight: s.setAbortInFlight
        })));
    useEffect(()=>{
        return ()=>{
            var _inFlight_controller;
            (_inFlight_controller = inFlight.controller) === null || _inFlight_controller === void 0 ? void 0 : _inFlight_controller.abort();
        };
    }, [
        inFlight
    ]);
    return useCallback(async (rawQuery)=>{
        var _inFlight_controller;
        const query = rawQuery.trim();
        if (query.length === 0) {
            return;
        }
        (_inFlight_controller = inFlight.controller) === null || _inFlight_controller === void 0 ? void 0 : _inFlight_controller.abort();
        const controller = new AbortController();
        inFlight.controller = controller;
        setAbortInFlight(()=>controller.abort());
        setSubmitting(true);
        try {
            const response = await api.draftsAiSearch.invoke({
                query,
                signal: controller.signal,
                timeoutMs: DRAFTS_AI_SEARCH_TIMEOUT_MS
            });
            if (controller.signal.aborted) return;
            if (response.tag === 'cannotExtract') {
                setError('cannot_extract');
                return;
            }
            setFilter(response.filter, query);
        } catch (err) {
            if (isAbortError(err)) return;
            if (controller.signal.aborted) return;
            if (isTimeoutError(err)) {
                setError('llm_timeout');
                return;
            }
            const errorResponse = new ErrorResponse(err);
            if (errorResponse.hasStatus([
                404
            ])) {
                setError('feature_disabled');
                return;
            }
            setError(errorCodeFromResponseBody(errorResponse.body));
        } finally{
            if (inFlight.controller === controller) {
                setSubmitting(false);
                setAbortInFlight(null);
                inFlight.controller = null;
            }
        }
    }, [
        inFlight,
        setAbortInFlight,
        setSubmitting,
        setError,
        setFilter
    ]);
};
